import { useGetProductGroupsQuery } from '@graphql/generated/graphql';
import logger from 'src/utils/logger';
import { useEffect } from 'react';
import { usePageLoaded } from 'src/hooks/usePageLoaded';

interface ProductGroup {
  id: string;
  name: string;
}

const useGetProductGroups = () => {
  const pageLoaded = usePageLoaded();
  const {
    data,
    error: queryError,
    isError,
    isLoading,
  } = useGetProductGroupsQuery(
    {},
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: pageLoaded,
    },
  );

  const graphqlError = data?.getProductGroups?.error;
  const error = queryError || graphqlError;
  const hasError = isError || !!graphqlError;
  const errorMessage = graphqlError?.errorMessage || (queryError as Error)?.message;

  useEffect(() => {
    if (hasError) {
      void logger.error(`Error occurred while retrieving list of product groups: ${errorMessage}`);
    }
  }, [hasError, errorMessage]);

  // use a filter() method with a type guard before the map(); ensures non-null filtered out
  const productGroupList: ProductGroup[] =
    data?.getProductGroups.productGroups
      ?.filter((group): group is NonNullable<typeof group> => group !== null)
      .map((group) => ({
        id: group.productGroupId,
        name: group.productGroupName,
      }))
      .sort((g1: ProductGroup, g2: ProductGroup) => g1.name.localeCompare(g2.name)) || [];

  return {
    productGroupList,
    isLoading,
    error,
    hasError,
    errorMessage,
    errorType: graphqlError?.errorType,
  };
};

export default useGetProductGroups;
